import * as React from 'react';
import { FC, useState, useCallback, useMemo } from 'react';
import { Form } from 'reactstrap';
import { useTranslations, getLanguage } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { getPrePaymentDefaults } from './prePaymentTemplateDefaults';
import { useDispatch } from 'react-redux';
import { ServicePrePaymentSettings, ServicePaymentReminderSettings, ServicePaymentEventSettings, ServiceAutoDeleteGroupSettings } from '../../../models/api-models';
import { CheckInputField, TextInputField, NumberInputField, MultipleCheckboxes, AutocompleteTextField, PlaceholderTextField } from '../../../components/Forms/fields';
import Typography from '../../../components/Typography';
import ButtonsContainer from '../../../components/ButtonsContainer';
import EmailIcon from '@material-ui/icons/Email';
import SmsIcon from '@material-ui/icons/Sms';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import DeleteIcon from '@material-ui/icons/Delete';
import styled from 'styled-components';
import { testReminderEmailAsync, testReminderSmsAsync, testReminderWhatsAppAsync } from '../../../ajax/requests';
import { getErrorMessage, isConflictError } from '../../../ajax/errors';
import { alertError, alertSuccess, alertGenericError } from '../../../store/alerts/actions';
import { TestCommunication } from '../../../models';
import { Chance } from 'chance';
import { getNowDateString } from '../../../shared/utils';
import AppButton from '../../../components/AppButton';

// Lazy load HtmlEditor (259 KB with ReactQuill) - only loads when needed
const HtmlEditor = React.lazy(() => import('../../Campaigns/HtmlEditor').then(m => ({ default: m.HtmlEditor })));


interface ChannelCardProps extends React.HTMLAttributes<HTMLDivElement> {
    $isActive: boolean;
    $isSelected?: boolean;
}
const ChannelCard = styled.div.attrs<ChannelCardProps>(props => ({ ...props }))<ChannelCardProps>`
    border: var(--border-thin) solid ${props => props.$isSelected ? 'var(--appBlue)' : 'var(--appLigthGrey)'};
    border-radius: 8px;
    overflow: hidden;
    transition: border-color 0.2s ease;
`;
interface ChannelHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    $isActive: boolean;
    $isSelected: boolean;
}
const ChannelHeader = styled.div.attrs<ChannelHeaderProps>(props => ({ ...props }))<ChannelHeaderProps>`
    display: flex;
    align-items: center;
    padding: var(--spacer-S);
    background-color: ${props => props.$isSelected ? 'var(--bg-hover-light)' : 'var(--bg-light)'};
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
    &:hover { background-color: var(--bg-hover-light); }
`;
const ChannelInfo = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    flex: 1;
    margin-left: var(--spacer-XS);
`;
const ChannelContent = styled.div`
    padding: var(--spacer-S);
    border-top: var(--border-thin) solid var(--appLigthGrey);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;
interface ChannelStatusProps extends React.HTMLAttributes<HTMLSpanElement> { $isActive: boolean; }
const ChannelStatus = styled.span<ChannelStatusProps>`
    font-size: 0.75rem;
    padding: 2px 8px;
    border-radius: 12px;
    background-color: ${props => props.$isActive ? 'var(--success-bg)' : 'transparent'};
    color: ${props => props.$isActive ? 'var(--success-color)' : 'var(--text-muted)'};
    border: var(--border-thin) solid ${props => props.$isActive ? 'var(--success-border)' : 'var(--appLigthGrey)'};
`;

interface SectionHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}
const SectionHeader = styled.div<SectionHeaderProps>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;
    &:hover { opacity: 0.8; }
`;
interface SectionStatusProps extends React.HTMLAttributes<HTMLSpanElement> { $isEnabled: boolean; }
const SectionStatus = styled.span<SectionStatusProps>`
    font-size: 0.75rem;
    color: ${props => props.$isEnabled ? 'var(--success-color)' : 'var(--text-muted)'};
    font-weight: 500;
    margin-left: var(--spacer-S);
`;

const TestSection = styled.div`
    display: flex;
    gap: var(--spacer-XS);
    align-items: center;
    flex-wrap: wrap;
`;

const TestActionButton = styled(AppButton)`
    && {
        display: inline-flex;
        align-items: center;
        gap: var(--spacer-XXS);
        white-space: nowrap;
        border: var(--border-thin) solid var(--brand-secondary);
        background-color: var(--brand-accent);
        color: var(--brand-secondary);
        font-weight: 600;
        transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    }
    &&:hover, &&:focus {
        border-color: var(--brand-secondary);
        background-color: var(--surface-brand-soft);
        color: var(--brand-secondary);
    }
    &&:disabled {
        border-color: var(--surface-border-subtle);
        background-color: var(--bg-light);
        color: var(--text-muted);
        opacity: 1;
    }
`;

const chance = new Chance();

const getChannelIcon = (type: 'sms' | 'whatsapp' | 'email') => {
    const iconStyle = { fontSize: '1.2rem', verticalAlign: 'middle', color: 'inherit' };
    if (type === 'sms') return <SmsIcon style={iconStyle} />;
    if (type === 'whatsapp') return <WhatsAppIcon style={{ ...iconStyle, color: '#25D366' }} />;
    return <EmailIcon style={iconStyle} />;
};

export interface PrePaymentSettingsProps {
    id: string;
    value?: ServicePrePaymentSettings;
    services: Record<string, string>;
    onSave: (value: ServicePrePaymentSettings) => void;
}

const PrePaymentSettings: FC<PrePaymentSettingsProps> = ({ id, value, services, onSave }) => {
    const t = useTranslations();
    const serviceNames = useMemo(() => Object.values(services), [services]);
    const idsToNames = useCallback((ids: string[]) => ids.map(i => services[i] ?? i), [services]);
    const namesToIds = useCallback((names: string[]) => names.map(n => Object.keys(services).find(k => services[k] === n) ?? n), [services]);
    const dispatch = useDispatch();
    const templates = getPrePaymentDefaults(getLanguage());

    const [prePayment, setPrePayment] = useState<ServicePrePaymentSettings>(() => value ?? {} as ServicePrePaymentSettings);
    const [emailTest, setEmailTest] = useState('');
    const [phoneNumberTest, setPhoneNumberTest] = useState('');
    const [testingPaymentReminder, setTestingPaymentReminder] = useState(false);
    const [testingPaymentEvent, setTestingPaymentEvent] = useState(false);
    const [activeEditorId, setActiveEditorId] = useState<string | null>(null);
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({ autoDelete: true, paymentReminder: true, paymentConfirmed: true, paymentCancelled: true });
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

    const toggleSection = (key: string) => setCollapsedSections(s => ({ ...s, [key]: !s[key] }));

    const toggleGroup = (index: number) => setExpandedGroups(s => {
        const next = new Set(s);
        next.has(index) ? next.delete(index) : next.add(index);
        return next;
    });

    const updateGroup = useCallback((index: number, updates: Partial<ServiceAutoDeleteGroupSettings>) =>
        setPrePayment(p => {
            const groups = [...(p.autoDeleteGroups ?? [])];
            groups[index] = { ...groups[index], ...updates };
            return { ...p, autoDeleteGroups: groups };
        }), []);

    const addGroup = useCallback(() => {
        setPrePayment(p => ({
            ...p,
            autoDeleteGroups: [...(p.autoDeleteGroups ?? []), { applicableServices: [] }],
        }));
        setExpandedGroups(s => new Set([...s, prePayment.autoDeleteGroups?.length ?? 0]));
    }, [prePayment.autoDeleteGroups?.length]);

    const removeGroup = useCallback((index: number) => {
        setPrePayment(p => {
            const groups = (p.autoDeleteGroups ?? []).filter((_, i) => i !== index);
            return { ...p, autoDeleteGroups: groups.length ? groups : undefined };
        });
        setExpandedGroups(s => {
            const next = new Set<number>();
            s.forEach(i => { if (i < index) next.add(i); else if (i > index) next.add(i - 1); });
            return next;
        });
    }, []);

    const hoursMap = useMemo(() => ({
        [t(TK.twoHours)]: 2,
        [t(TK.fourHours)]: 4,
        [t(TK.oneDay)]: 24,
        [t(TK.twoDays)]: 24 * 2,
        [t(TK.threeDays)]: 24 * 3,
        [t(TK.oneWeek)]: 24 * 7,
    }), [t]);

    const paymentReminder: ServicePaymentReminderSettings = prePayment.paymentReminder ?? {
        smsHoursBeforeAppointment: [],
        whatsAppHoursBeforeAppointment: [],
        emailHoursBeforeAppointment: [],
    };

    const isPaymentChannelActive = (channel: 'sms' | 'whatsapp' | 'email') => {
        if (channel === 'sms') return !!(paymentReminder.smsHoursBeforeAppointment?.length || paymentReminder.smsOnAppointmentCreation);
        if (channel === 'whatsapp') return !!(paymentReminder.whatsAppHoursBeforeAppointment?.length || paymentReminder.whatsAppOnAppointmentCreation);
        return !!(paymentReminder.emailHoursBeforeAppointment?.length || paymentReminder.emailOnAppointmentCreation);
    };

    const updatePaymentReminder = useCallback((updates: Partial<ServicePaymentReminderSettings>) =>
        setPrePayment(p => ({
            ...p,
            paymentReminder: { ...(p.paymentReminder ?? {}), ...updates },
        }) as ServicePrePaymentSettings), []);

    const disablePaymentChannel = (channel: 'sms' | 'whatsapp' | 'email') => {
        if (channel === 'sms') updatePaymentReminder({ smsHoursBeforeAppointment: [], smsOnAppointmentCreation: false });
        else if (channel === 'whatsapp') updatePaymentReminder({ whatsAppHoursBeforeAppointment: [], whatsAppOnAppointmentCreation: false });
        else updatePaymentReminder({ emailHoursBeforeAppointment: [], emailOnAppointmentCreation: false });
    };

    const enablePaymentChannelDefaults = (channel: 'sms' | 'whatsapp' | 'email') => {
        if (channel === 'sms' && !isPaymentChannelActive('sms'))
            updatePaymentReminder({ smsHoursBeforeAppointment: [24], smsText: templates.paymentReminder.smsText });
        else if (channel === 'whatsapp' && !isPaymentChannelActive('whatsapp'))
            updatePaymentReminder({ whatsAppHoursBeforeAppointment: [24] });
        else if (channel === 'email' && !isPaymentChannelActive('email'))
            updatePaymentReminder({ emailHoursBeforeAppointment: [24], emailName: templates.paymentReminder.emailName, emailSubject: templates.paymentReminder.emailSubject, emailText: templates.paymentReminder.emailText });
    };

    const buildPaymentTestData = (receiver: string): TestCommunication => ({
        receiverEmailOrPhoneNumber: receiver,
        remindersSetting: {
            smsText: paymentReminder.smsText,
            smsOnAppointmentCreation: paymentReminder.smsOnAppointmentCreation,
            smsHoursBeforeAppointment: paymentReminder.smsHoursBeforeAppointment ?? [],
            whatsAppText: templates.paymentReminder.whatsAppText,
            whatsAppOnAppointmentCreation: paymentReminder.whatsAppOnAppointmentCreation,
            whatsAppHoursBeforeAppointment: paymentReminder.whatsAppHoursBeforeAppointment ?? [],
            emailName: paymentReminder.emailName,
            emailSubject: paymentReminder.emailSubject,
            emailText: paymentReminder.emailText,
            emailOnAppointmentCreation: paymentReminder.emailOnAppointmentCreation,
            emailHoursBeforeAppointment: paymentReminder.emailHoursBeforeAppointment ?? [],
        },
        appointment: {
            id: '',
            clientId: chance.guid(),
            clientName: chance.name(),
            professionalId: chance.guid(),
            professionalName: chance.name(),
            creationDate: getNowDateString(),
            date: getNowDateString(),
            start: '09:00',
            end: '09:30',
            room: 'Gabinete 1',
            price: 30,
            type: 'Consulta',
            payments: [],
            expenses: [],
            vat: 0,
            confirmationRequestsSentBySms: [],
            confirmationRequestsSentByWhatsapp: [],
        },
        isConfirmationRequest: false,
    });

    const catchTestError = (error: unknown) => {
        const message = getErrorMessage(error);
        if (message) { dispatch(alertError(message)); return; }
        dispatch(isConflictError(error) ? alertError(t(TK.plafontExausted)) : alertGenericError());
    };

    const handleTestPaymentSms = () => {
        if (!/^((\+\d{12})|(\d{9}))$/.test(phoneNumberTest)) { dispatch(alertError(t(TK.invalidPhoneNumber))); return; }
        setTestingPaymentReminder(true);
        testReminderSmsAsync(buildPaymentTestData(phoneNumberTest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchTestError)
            .finally(() => setTestingPaymentReminder(false));
    };

    const handleTestPaymentWhatsApp = () => {
        if (!/^((\+\d{12})|(\d{9}))$/.test(phoneNumberTest)) { dispatch(alertError(t(TK.invalidPhoneNumber))); return; }
        setTestingPaymentReminder(true);
        testReminderWhatsAppAsync(buildPaymentTestData(phoneNumberTest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchTestError)
            .finally(() => setTestingPaymentReminder(false));
    };

    const handleTestPaymentEmail = () => {
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(emailTest)) { dispatch(alertError(t(TK.invalidEmail))); return; }
        setTestingPaymentReminder(true);
        testReminderEmailAsync(buildPaymentTestData(emailTest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchTestError)
            .finally(() => setTestingPaymentReminder(false));
    };

    const buildEventTestData = (event: ServicePaymentEventSettings, whatsAppTemplate: string, receiver: string): TestCommunication => ({
        receiverEmailOrPhoneNumber: receiver,
        remindersSetting: {
            smsText: event.smsText,
            smsHoursBeforeAppointment: [],
            whatsAppText: whatsAppTemplate,
            whatsAppHoursBeforeAppointment: [],
            emailName: event.emailName,
            emailSubject: event.emailSubject,
            emailText: event.emailText,
            emailHoursBeforeAppointment: [],
        },
        appointment: {
            id: '',
            clientId: chance.guid(),
            clientName: chance.name(),
            professionalId: chance.guid(),
            professionalName: chance.name(),
            creationDate: getNowDateString(),
            date: getNowDateString(),
            start: '09:00',
            end: '09:30',
            room: 'Gabinete 1',
            price: 30,
            type: 'Consulta',
            payments: [],
            expenses: [],
            vat: 0,
            confirmationRequestsSentBySms: [],
            confirmationRequestsSentByWhatsapp: [],
        },
        isConfirmationRequest: false,
    });

    const handleTestEventSms = (event: ServicePaymentEventSettings, whatsAppTemplate: string) => {
        if (!/^((\+\d{12})|(\d{9}))$/.test(phoneNumberTest)) { dispatch(alertError(t(TK.invalidPhoneNumber))); return; }
        setTestingPaymentEvent(true);
        testReminderSmsAsync(buildEventTestData(event, whatsAppTemplate, phoneNumberTest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchTestError)
            .finally(() => setTestingPaymentEvent(false));
    };

    const handleTestEventWhatsApp = (event: ServicePaymentEventSettings, whatsAppTemplate: string) => {
        if (!/^((\+\d{12})|(\d{9}))$/.test(phoneNumberTest)) { dispatch(alertError(t(TK.invalidPhoneNumber))); return; }
        setTestingPaymentEvent(true);
        testReminderWhatsAppAsync(buildEventTestData(event, whatsAppTemplate, phoneNumberTest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchTestError)
            .finally(() => setTestingPaymentEvent(false));
    };

    const handleTestEventEmail = (event: ServicePaymentEventSettings, whatsAppTemplate: string) => {
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(emailTest)) { dispatch(alertError(t(TK.invalidEmail))); return; }
        setTestingPaymentEvent(true);
        testReminderEmailAsync(buildEventTestData(event, whatsAppTemplate, emailTest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchTestError)
            .finally(() => setTestingPaymentEvent(false));
    };

    const renderEmailBody = (editorId: string, value: string, onChange: (v: string) => void) => {
        if (activeEditorId === editorId) {
            return (
                <React.Suspense fallback={<div style={{ padding: 'var(--spacer-M)', textAlign: 'center' }}>Loading editor...</div>}>
                    <HtmlEditor value={value} onChange={onChange} />
                </React.Suspense>
            );
        }
        return (
            <div
                style={{ border: 'var(--border-thin) solid var(--appLigthGrey)', borderRadius: 4, padding: 'var(--spacer-S)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.875rem', minHeight: '60px', backgroundColor: 'var(--bg-light)' }}
                onClick={() => setActiveEditorId(editorId)}
                dangerouslySetInnerHTML={{ __html: value || '<em>Click to edit...</em>' }}
            />
        );
    };

    const renderPaymentChannelConfig = (channel: 'sms' | 'whatsapp' | 'email') => {
        const onCreation = channel === 'sms' ? paymentReminder.smsOnAppointmentCreation
            : channel === 'whatsapp' ? paymentReminder.whatsAppOnAppointmentCreation
            : paymentReminder.emailOnAppointmentCreation;
        const hours = channel === 'sms' ? paymentReminder.smsHoursBeforeAppointment
            : channel === 'whatsapp' ? paymentReminder.whatsAppHoursBeforeAppointment
            : paymentReminder.emailHoursBeforeAppointment;
        const onCreationKey = channel === 'sms' ? 'smsOnAppointmentCreation'
            : channel === 'whatsapp' ? 'whatsAppOnAppointmentCreation'
            : 'emailOnAppointmentCreation';
        const hoursKey = channel === 'sms' ? 'smsHoursBeforeAppointment'
            : channel === 'whatsapp' ? 'whatsAppHoursBeforeAppointment'
            : 'emailHoursBeforeAppointment';

        const timingSection = (
            <>
                <Typography size="large"><b>{t(TK.whenToSend)}</b></Typography>
                <CheckInputField
                    label={t(TK.onCreationAndEdition)}
                    value={onCreation ?? false}
                    onChange={v => updatePaymentReminder({ [onCreationKey]: v })}
                />
                <MultipleCheckboxes
                    values={hours?.map(h => Object.keys(hoursMap).find(k => hoursMap[k] === h) ?? '') ?? []}
                    options={Object.keys(hoursMap)}
                    onChange={opts => updatePaymentReminder({ [hoursKey]: opts.map(o => hoursMap[o]) })}
                />
            </>
        );

        if (channel === 'sms') return (
            <>
                <PlaceholderTextField
                    id={`payment-reminder-sms-text`}
                    label={t(TK.message)}
                    value={paymentReminder.smsText ?? ''}
                    type='textarea'
                    maxLength={500}
                    onChange={smsText => updatePaymentReminder({ smsText })}
                />
                {timingSection}
                <Typography size="large"><b>{t(TK.testReminder)}</b></Typography>
                <TestSection>
                    <TextInputField
                        style={{ flexGrow: 1, minWidth: '200px' }}
                        value={phoneNumberTest}
                        onChange={setPhoneNumberTest}
                        placeholder="+351910000000"
                    />
                    <TestActionButton disabled={testingPaymentReminder} onClick={handleTestPaymentSms}>
                        <SmsIcon style={{ fontSize: '1.2rem' }} />
                        {t(TK.testSms)}
                    </TestActionButton>
                </TestSection>
            </>
        );

        if (channel === 'whatsapp') return (
            <>
                <TextInputField
                    label={t(TK.message)}
                    value={templates.paymentReminder.whatsAppText}
                    type='textarea'
                    disabled
                />
                {timingSection}
                <Typography size="large"><b>{t(TK.testReminder)}</b></Typography>
                <TestSection>
                    <TextInputField
                        style={{ flexGrow: 1, minWidth: '200px' }}
                        value={phoneNumberTest}
                        onChange={setPhoneNumberTest}
                        placeholder="+351910000000"
                    />
                    <TestActionButton disabled={testingPaymentReminder} onClick={handleTestPaymentWhatsApp}>
                        <WhatsAppIcon style={{ fontSize: '1.2rem', color: '#25D366' }} />
                        {t(TK.testWhatsApp)}
                    </TestActionButton>
                </TestSection>
            </>
        );

        return (
            <>
                <TextInputField
                    label={t(TK.fromName)}
                    value={paymentReminder.emailName ?? ''}
                    onChange={emailName => updatePaymentReminder({ emailName })}
                />
                <TextInputField
                    label={t(TK.subject)}
                    value={paymentReminder.emailSubject ?? ''}
                    onChange={emailSubject => updatePaymentReminder({ emailSubject })}
                />
                <Typography size="large"><b>{t(TK.message)}</b></Typography>
                {renderEmailBody('reminder', paymentReminder.emailText ?? '', emailText => updatePaymentReminder({ emailText }))}
                {timingSection}
                <Typography size="large"><b>{t(TK.testReminder)}</b></Typography>
                <TestSection>
                    <TextInputField
                        style={{ flexGrow: 1, minWidth: '200px' }}
                        value={emailTest}
                        onChange={setEmailTest}
                        placeholder="xpto@domain.com"
                    />
                    <TestActionButton disabled={testingPaymentReminder} onClick={handleTestPaymentEmail}>
                        <EmailIcon style={{ fontSize: '1.2rem' }} />
                        {t(TK.testEmail)}
                    </TestActionButton>
                </TestSection>
            </>
        );
    };

    const updatePaymentEvent = useCallback((key: 'paymentConfirmed' | 'paymentCancelled', updates: Partial<ServicePaymentEventSettings>) =>
        setPrePayment(p => ({
            ...p,
            [key]: { ...(p[key] ?? {}), ...updates },
        })), []);

    const renderPaymentEventConfig = (
        key: 'paymentConfirmed' | 'paymentCancelled',
        whatsAppTemplate: string,
    ) => {
        const event = prePayment[key] ?? {};
        const update = (updates: Partial<ServicePaymentEventSettings>) => updatePaymentEvent(key, updates);

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
                {(['sms', 'whatsapp', 'email'] as const).map(channel => {
                    const active = channel === 'sms' ? (event.smsEnabled ?? false)
                        : channel === 'whatsapp' ? (event.whatsAppEnabled ?? false)
                        : (event.emailEnabled ?? false);
                    const channelLabel = channel === 'sms' ? t(TK.sms) : channel === 'whatsapp' ? 'WhatsApp' : t(TK.email);
                    const enableKey = channel === 'sms' ? 'smsEnabled' : channel === 'whatsapp' ? 'whatsAppEnabled' : 'emailEnabled';

                    const handleEventChannelToggle = (checked: boolean) => {
                        if (!checked) { update({ [enableKey]: false }); return; }
                        const eventDefaults = templates[key];
                        if (channel === 'sms') update({ smsEnabled: true, smsText: eventDefaults.smsText });
                        else if (channel === 'email') update({ emailEnabled: true, emailName: eventDefaults.emailName, emailSubject: eventDefaults.emailSubject, emailText: eventDefaults.emailText });
                        else update({ [enableKey]: true });
                    };

                    return (
                        <ChannelCard key={channel} $isActive={active}>
                            <ChannelHeader $isActive={active} $isSelected={false}>
                                <CheckInputField
                                    label=""
                                    value={active}
                                    onChange={handleEventChannelToggle}
                                />
                                <ChannelInfo>
                                    {getChannelIcon(channel)}
                                    <Typography><b>{channelLabel}</b></Typography>
                                    <ChannelStatus $isActive={active}>
                                        {active ? t(TK.active) : t(TK.inactive)}
                                    </ChannelStatus>
                                </ChannelInfo>
                            </ChannelHeader>
                            {active && (
                                <ChannelContent>
                                    {channel === 'sms' && (
                                        <PlaceholderTextField
                                            id={`payment-event-${key}-sms-text`}
                                            label={t(TK.message)}
                                            value={event.smsText ?? ''}
                                            type='textarea'
                                            maxLength={500}
                                            onChange={smsText => update({ smsText })}
                                        />
                                    )}
                                    {channel === 'whatsapp' && (
                                        <TextInputField
                                            label={t(TK.message)}
                                            value={whatsAppTemplate}
                                            type='textarea'
                                            disabled
                                        />
                                    )}
                                    {channel === 'email' && (<>
                                        <PlaceholderTextField
                                            id={`payment-event-${key}-email-name`}
                                            label={t(TK.fromName)}
                                            value={event.emailName ?? ''}
                                            onChange={emailName => update({ emailName })}
                                        />
                                        <PlaceholderTextField
                                            id={`payment-event-${key}-email-subject`}
                                            label={t(TK.subject)}
                                            value={event.emailSubject ?? ''}
                                            onChange={emailSubject => update({ emailSubject })}
                                        />
                                        <Typography size="large"><b>{t(TK.message)}</b></Typography>
                                        {renderEmailBody(key, event.emailText ?? '', emailText => update({ emailText }))}
                                    </>)}
                                    <Typography size="large"><b>{t(TK.testReminder)}</b></Typography>
                                    <TestSection>
                                        {channel === 'email' ? (<>
                                            <TextInputField
                                                style={{ flexGrow: 1, minWidth: '200px' }}
                                                value={emailTest}
                                                onChange={setEmailTest}
                                                placeholder="xpto@domain.com"
                                            />
                                            <TestActionButton disabled={testingPaymentEvent} onClick={() => handleTestEventEmail(event, whatsAppTemplate)}>
                                                <EmailIcon style={{ fontSize: '1.2rem' }} />
                                                {t(TK.testEmail)}
                                            </TestActionButton>
                                        </>) : (<>
                                            <TextInputField
                                                style={{ flexGrow: 1, minWidth: '200px' }}
                                                value={phoneNumberTest}
                                                onChange={setPhoneNumberTest}
                                                placeholder="+351910000000"
                                            />
                                            {channel === 'sms' && (
                                                <TestActionButton disabled={testingPaymentEvent} onClick={() => handleTestEventSms(event, whatsAppTemplate)}>
                                                    <SmsIcon style={{ fontSize: '1.2rem' }} />
                                                    {t(TK.testSms)}
                                                </TestActionButton>
                                            )}
                                            {channel === 'whatsapp' && (
                                                <TestActionButton disabled={testingPaymentEvent} onClick={() => handleTestEventWhatsApp(event, whatsAppTemplate)}>
                                                    <WhatsAppIcon style={{ fontSize: '1.2rem', color: '#25D366' }} />
                                                    {t(TK.testWhatsApp)}
                                                </TestActionButton>
                                            )}
                                        </>)}
                                    </TestSection>
                                </ChannelContent>
                            )}
                        </ChannelCard>
                    );
                })}
            </div>
        );
    };

    return (
        <Form style={{ display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)' }}>

            <CheckInputField
                label={t(TK.prePaymentEnabled)}
                helpText={t(TK.prePaymentEnabledHelp)}
                value={prePayment.enabled ?? false}
                onChange={enabled => setPrePayment(p => ({ ...p, enabled }))}
            />

            {(prePayment.enabled ?? false) && (<>
                <AutocompleteTextField
                    label={t(TK.applicableServices)}
                    helpText={t(TK.applicableServicesHelp)}
                    multiSelect
                    values={idsToNames(prePayment.applicableServices ?? [])}
                    options={serviceNames}
                    placeholder={prePayment.applicableServices?.length ? undefined : t(TK.applicableServicesPlaceholder)}
                    onValuesChanged={names => setPrePayment(p => ({ ...p, applicableServices: names.length ? namesToIds(names) : undefined }))}
                />
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', padding: 'var(--spacer-S)', border: 'var(--border-thin) solid var(--appLigthGrey)', borderRadius: 4 }}>
                    <SectionHeader onClick={() => toggleSection('autoDelete')}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography size="large"><b>{t(TK.autoDeleteIfNotPaid)}</b></Typography>
                            <SectionStatus $isEnabled={prePayment.autoDeleteIfNotPaid ?? false}>
                                {(prePayment.autoDeleteIfNotPaid ?? false) ? t(TK.active) : t(TK.inactive)}
                            </SectionStatus>
                        </div>
                        {collapsedSections['autoDelete'] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </SectionHeader>
                    {!collapsedSections['autoDelete'] && (<>
                        <CheckInputField
                            label={t(TK.autoDeleteIfNotPaid)}
                            helpText={t(TK.autoDeleteIfNotPaidHelp)}
                            value={prePayment.autoDeleteIfNotPaid ?? false}
                            onChange={autoDeleteIfNotPaid => setPrePayment(p => ({ ...p, autoDeleteIfNotPaid }))}
                        />
                        {(prePayment.autoDeleteIfNotPaid ?? false) && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', paddingLeft: 'var(--spacer-M)' }}>
                                <NumberInputField
                                    id={`${id}-prepay-days-after-creation`}
                                    label={t(TK.autoDeleteDaysAfterCreation)}
                                    helpText={t(TK.autoDeleteDaysAfterCreationHelp)}
                                    append={t(TK.days)}
                                    min={1}
                                    value={prePayment.autoDeleteDaysAfterCreation ?? undefined}
                                    onChange={v => setPrePayment(p => ({ ...p, autoDeleteDaysAfterCreation: v || undefined }))}
                                />
                                <NumberInputField
                                    id={`${id}-prepay-days-before-appointment`}
                                    label={t(TK.autoDeleteDaysBeforeAppointment)}
                                    helpText={t(TK.autoDeleteDaysBeforeAppointmentHelp)}
                                    min={1}
                                    append={t(TK.days)}
                                    value={prePayment.autoDeleteDaysBeforeAppointment ?? undefined}
                                    onChange={v => setPrePayment(p => ({ ...p, autoDeleteDaysBeforeAppointment: v || undefined }))}
                                />
                                <NumberInputField
                                    id={`${id}-prepay-day-of-month`}
                                    label={t(TK.autoDeleteIfNotPaidOnDayOfMonth)}
                                    helpText={t(TK.autoDeleteIfNotPaidOnDayOfMonthHelp)}
                                    min={1}
                                    max={28}
                                    value={prePayment.autoDeleteIfNotPaidOnDayOfMonth ?? undefined}
                                    onChange={v => setPrePayment(p => ({ ...p, autoDeleteIfNotPaidOnDayOfMonth: v || undefined }))}
                                />

                                <hr style={{ width: '100%', margin: 'var(--spacer-XS) 0' }} />

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
                                        <Typography size="large"><b>{t(TK.autoDeleteGroups)}</b></Typography>
                                        <Typography size="smaller" style={{ color: 'var(--text-muted)', marginTop: 'var(--spacer-XXS)' }}>
                                            {t(TK.autoDeleteGroupsHelp)}
                                        </Typography>
                                    </div>

                                    {(prePayment.autoDeleteGroups ?? []).map((group, index) => {
                                        const isExpanded = expandedGroups.has(index);
                                        const isDisabled = group.autoDeleteIfNotPaid === false;
                                        const groupLabel = group.applicableServices?.length
                                            ? idsToNames(group.applicableServices).join(', ')
                                            : `${t(TK.group)} ${index + 1}`;
                                        return (
                                            <div key={index} style={{ border: 'var(--border-thin) solid var(--appLigthGrey)', borderRadius: 4 }}>
                                                <div
                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--spacer-XS) var(--spacer-S)', cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => toggleGroup(index)}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)', flex: 1, minWidth: 0 }}>
                                                        <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{groupLabel}</span>
                                                        {isDisabled && (
                                                            <SectionStatus $isEnabled={false} style={{ flexShrink: 0 }}>
                                                                {t(TK.inactive)}
                                                            </SectionStatus>
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)', flexShrink: 0 }}>
                                                        <AppButton
                                                            color="link"
                                                            size="sm"
                                                            style={{ padding: 0, color: 'var(--danger-color)' }}
                                                            onClick={e => { e.stopPropagation(); removeGroup(index); }}
                                                        >
                                                            <DeleteIcon style={{ fontSize: '1.2rem' }} />
                                                        </AppButton>
                                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </div>
                                                </div>
                                                {isExpanded && (
                                                    <div style={{ borderTop: 'var(--border-thin) solid var(--appLigthGrey)', padding: 'var(--spacer-S)', display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                                                        <AutocompleteTextField
                                                            label={t(TK.applicableServices)}
                                                            multiSelect
                                                            values={idsToNames(group.applicableServices ?? [])}
                                                            options={serviceNames}
                                                            onValuesChanged={names => updateGroup(index, { applicableServices: namesToIds(names) })}
                                                        />
                                                        <CheckInputField
                                                            label={t(TK.disableAutoDeleteForGroup)}
                                                            value={group.autoDeleteIfNotPaid === false}
                                                            onChange={checked => updateGroup(index, { autoDeleteIfNotPaid: checked ? false : undefined })}
                                                        />
                                                        {group.autoDeleteIfNotPaid !== false && (<>
                                                            <NumberInputField
                                                                id={`${id}-group-${index}-days-after-creation`}
                                                                label={t(TK.autoDeleteDaysAfterCreation)}
                                                                helpText={t(TK.autoDeleteDaysAfterCreationHelp)}
                                                                append={t(TK.days)}
                                                                min={1}
                                                                value={group.autoDeleteDaysAfterCreation ?? undefined}
                                                                onChange={v => updateGroup(index, { autoDeleteDaysAfterCreation: v || undefined })}
                                                            />
                                                            <NumberInputField
                                                                id={`${id}-group-${index}-days-before-appointment`}
                                                                label={t(TK.autoDeleteDaysBeforeAppointment)}
                                                                helpText={t(TK.autoDeleteDaysBeforeAppointmentHelp)}
                                                                min={1}
                                                                append={t(TK.days)}
                                                                value={group.autoDeleteDaysBeforeAppointment ?? undefined}
                                                                onChange={v => updateGroup(index, { autoDeleteDaysBeforeAppointment: v || undefined })}
                                                            />
                                                            <NumberInputField
                                                                id={`${id}-group-${index}-day-of-month`}
                                                                label={t(TK.autoDeleteIfNotPaidOnDayOfMonth)}
                                                                helpText={t(TK.autoDeleteIfNotPaidOnDayOfMonthHelp)}
                                                                min={1}
                                                                max={28}
                                                                value={group.autoDeleteIfNotPaidOnDayOfMonth ?? undefined}
                                                                onChange={v => updateGroup(index, { autoDeleteIfNotPaidOnDayOfMonth: v || undefined })}
                                                            />
                                                        </>)}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    <AppButton color="link" style={{ alignSelf: 'flex-start', padding: 0 }} onClick={addGroup}>
                                        + {t(TK.addGroup)}
                                    </AppButton>
                                </div>
                            </div>
                        )}
                    </>)}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)', padding: 'var(--spacer-S)', border: 'var(--border-thin) solid var(--appLigthGrey)', borderRadius: 4 }}>
                    <SectionHeader onClick={() => toggleSection('paymentReminder')}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography size="large"><b>{t(TK.paymentReminder)}</b></Typography>
                            <SectionStatus $isEnabled={isPaymentChannelActive('sms') || isPaymentChannelActive('whatsapp') || isPaymentChannelActive('email')}>
                                {(isPaymentChannelActive('sms') || isPaymentChannelActive('whatsapp') || isPaymentChannelActive('email')) ? t(TK.active) : t(TK.inactive)}
                            </SectionStatus>
                        </div>
                        {collapsedSections['paymentReminder'] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </SectionHeader>
                    {!collapsedSections['paymentReminder'] && (<>
                        <Typography size="smaller" style={{ color: 'var(--text-muted)' }}>{t(TK.paymentReminderHelp)}</Typography>
                        {(['sms', 'whatsapp', 'email'] as const).map(channel => {
                            const active = isPaymentChannelActive(channel);
                            const label = channel === 'sms' ? t(TK.sms) : channel === 'whatsapp' ? 'WhatsApp' : t(TK.email);
                            return (
                                <ChannelCard key={channel} $isActive={active}>
                                    <ChannelHeader $isActive={active} $isSelected={false}>
                                        <CheckInputField
                                            label=""
                                            value={active}
                                            onChange={checked => checked ? enablePaymentChannelDefaults(channel) : disablePaymentChannel(channel)}
                                        />
                                        <ChannelInfo>
                                            {getChannelIcon(channel)}
                                            <Typography><b>{label}</b></Typography>
                                            <ChannelStatus $isActive={active}>
                                                {active ? t(TK.active) : t(TK.inactive)}
                                            </ChannelStatus>
                                        </ChannelInfo>
                                    </ChannelHeader>
                                    {active && (
                                        <ChannelContent>{renderPaymentChannelConfig(channel)}</ChannelContent>
                                    )}
                                </ChannelCard>
                            );
                        })}
                    </>)}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', padding: 'var(--spacer-S)', border: 'var(--border-thin) solid var(--appLigthGrey)', borderRadius: 4 }}>
                    <SectionHeader onClick={() => toggleSection('paymentConfirmed')}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography size="large"><b>{t(TK.paymentConfirmedNotification)}</b></Typography>
                            <SectionStatus $isEnabled={!!(prePayment.paymentConfirmed?.smsEnabled || prePayment.paymentConfirmed?.whatsAppEnabled || prePayment.paymentConfirmed?.emailEnabled)}>
                                {(prePayment.paymentConfirmed?.smsEnabled || prePayment.paymentConfirmed?.whatsAppEnabled || prePayment.paymentConfirmed?.emailEnabled) ? t(TK.active) : t(TK.inactive)}
                            </SectionStatus>
                        </div>
                        {collapsedSections['paymentConfirmed'] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </SectionHeader>
                    {!collapsedSections['paymentConfirmed'] && (<>
                        <Typography size="smaller" style={{ color: 'var(--text-muted)' }}>{t(TK.paymentConfirmedNotificationHelp)}</Typography>
                        {renderPaymentEventConfig('paymentConfirmed', templates.paymentConfirmed.whatsAppText)}
                    </>)}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', padding: 'var(--spacer-S)', border: 'var(--border-thin) solid var(--appLigthGrey)', borderRadius: 4 }}>
                    <SectionHeader onClick={() => toggleSection('paymentCancelled')}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography size="large"><b>{t(TK.paymentCancelledNotification)}</b></Typography>
                            <SectionStatus $isEnabled={!!(prePayment.paymentCancelled?.smsEnabled || prePayment.paymentCancelled?.whatsAppEnabled || prePayment.paymentCancelled?.emailEnabled)}>
                                {(prePayment.paymentCancelled?.smsEnabled || prePayment.paymentCancelled?.whatsAppEnabled || prePayment.paymentCancelled?.emailEnabled) ? t(TK.active) : t(TK.inactive)}
                            </SectionStatus>
                        </div>
                        {collapsedSections['paymentCancelled'] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </SectionHeader>
                    {!collapsedSections['paymentCancelled'] && (<>
                        <Typography size="smaller" style={{ color: 'var(--text-muted)' }}>{t(TK.paymentCancelledNotificationHelp)}</Typography>
                        {renderPaymentEventConfig('paymentCancelled', templates.paymentCancelled.whatsAppText)}
                    </>)}
                </div>
            </>)}

            <ButtonsContainer>
                <AppButton color="success" onClick={() => onSave(prePayment)}>
                    {t(TK.save)}
                </AppButton>
            </ButtonsContainer>
        </Form>
    );
};

export default PrePaymentSettings;
